import { useTranslation } from "react-i18next"

import type { HostedAgentFormErrors, HostedAgentFormState } from "../utils/hosted-agents"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { AgentCharacter } from "@/features/agents/components/agent-character"
import { agentCharacterIds } from "@/features/agents/utils/agent-character-utils"
import { agentCharacter } from "@/features/agents/utils/agent-presence-utils"

export function HostedAgentProfileTab({
  form,
  patch,
  errors,
  agentId = "preview",
}: {
  form: HostedAgentFormState
  patch: (partial: Partial<HostedAgentFormState>) => void
  errors: HostedAgentFormErrors
  agentId?: string
}) {
  const { t } = useTranslation()
  const label = (key: string) => t(`adminDashboard.hostedAgents.product.${key}`)
  const selectedCharacter = form.avatarCharacter || agentCharacterIds[agentCharacter(agentId)]
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="hosted-agent-name">{label("name")}</Label>
          <Input
            required
            aria-invalid={Boolean(errors.name)}
            id="hosted-agent-name"
            maxLength={120}
            value={form.name}
            onChange={(event) => patch({ name: event.target.value })}
          />
          {errors.name && (
            <p
              className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-sm"
              role="alert"
            >
              {label("nameRequired")}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="hosted-agent-title">{label("role")}</Label>
          <Input
            id="hosted-agent-title"
            maxLength={200}
            value={form.title}
            onChange={(event) => patch({ title: event.target.value })}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="hosted-agent-introduction">{label("introduction")}</Label>
        <Textarea
          aria-describedby="agent-introduction-help"
          id="hosted-agent-introduction"
          maxLength={240}
          rows={2}
          value={form.introduction}
          onChange={(event) => patch({ introduction: event.target.value })}
        />
        <p className="text-muted-foreground text-xs" id="agent-introduction-help">
          {label("introductionHelp")}
        </p>
      </div>
      <fieldset className="space-y-2">
        <legend className="mb-2 text-sm font-medium">{label("starters")}</legend>
        {[0, 1, 2].map((index) => (
          <Input
            key={index}
            aria-label={`${label("starter")} ${index + 1}`}
            maxLength={200}
            value={form.starterPrompts[index] ?? ""}
            onChange={(event) => {
              const starterPrompts = [...form.starterPrompts]
              starterPrompts[index] = event.target.value
              patch({ starterPrompts })
            }}
          />
        ))}
        <p className="text-muted-foreground text-xs">{label("startersHelp")}</p>
      </fieldset>
      <fieldset>
        <legend className="mb-3 text-sm font-medium">{label("character")}</legend>
        <div
          aria-label={label("character")}
          className="grid grid-cols-4 gap-2 sm:grid-cols-8"
          role="radiogroup"
        >
          {agentCharacterIds.map((character) => (
            <label
              key={character}
              className="has-[:checked]:border-primary has-[:checked]:bg-accent relative flex cursor-pointer flex-col items-center rounded-md border p-2 text-xs"
            >
              <input
                checked={selectedCharacter === character}
                className="peer sr-only"
                name="hosted-agent-character"
                type="radio"
                value={character}
                onChange={() => patch({ avatarCharacter: character })}
              />
              <span className="peer-focus-visible:ring-ring rounded-md peer-focus-visible:ring-2">
                <AgentCharacter character={character} className="size-12" state="idle" />
              </span>
              <span className="mt-1 capitalize">{character}</span>
            </label>
          ))}
        </div>
      </fieldset>
      <details className="rounded-md border p-3" open={Boolean(errors.avatarColor)}>
        <summary className="cursor-pointer text-sm font-medium">{label("routing")}</summary>
        <div className="mt-3 space-y-2">
          <Label htmlFor="hosted-agent-description">{label("routingDescription")}</Label>
          <Textarea
            id="hosted-agent-description"
            maxLength={4000}
            rows={4}
            value={form.description}
            onChange={(event) => patch({ description: event.target.value })}
          />
          <p className="text-muted-foreground text-xs">{label("routingHelp")}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="hosted-agent-avatar-color">{label("legacyColor")}</Label>
              <Input
                aria-invalid={Boolean(errors.avatarColor)}
                id="hosted-agent-avatar-color"
                maxLength={7}
                value={form.avatarColor}
                onChange={(event) => patch({ avatarColor: event.target.value })}
              />
              {errors.avatarColor && (
                <p
                  className="bg-destructive text-destructive-foreground border-foreground rounded-md border px-2 py-1 text-xs"
                  role="alert"
                >
                  {label("invalidColor")}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="hosted-agent-avatar-initials">{label("legacyInitials")}</Label>
              <Input
                id="hosted-agent-avatar-initials"
                maxLength={3}
                value={form.avatarInitials}
                onChange={(event) => patch({ avatarInitials: event.target.value })}
              />
            </div>
          </div>
        </div>
      </details>
    </div>
  )
}
